// The season record: every match the screen showed, kept as a small summary once it is played,
// so the club can look back over the season in Studio. Player names are copied in when the match
// is saved, so the record stays right after someone leaves the squad or changes their name.
import { matchSummary, minuteOf, type MatchState, type Side } from './match';
import { getRules } from './rules';
import type { Club } from './club';

export interface RecordSide {
  name: string; short: string; color: string; crest: string | null;
  points: number;
  /** tries (rugby) or goals (football) */
  tries: number;
  /** each kind of score with how many: [{label:'Try', n:4}, {label:'Conversion', n:3}] */
  counts: { label: string; n: number }[];
  /** try or goal scorers with the minutes, most first; own goals are listed as "Own goal" */
  scorers: { name: string; n: number; mins: string[] }[];
  bonus: ('try' | 'losing')[];
  cards: { label: string; who: string; min: string }[];
  /** today's team as Matchday setup had it, in shirt order */
  team: { number: string; name: string }[];
}

export interface MatchRecord {
  v: 1;
  id: string;                     // the match id
  date: number;                   // when the match was set up (ms)
  season: string;                 // '2026-27'
  sport: string;
  squad: string | null; squadLabel: string | null;
  league: boolean;
  finished: boolean;              // false: ended before the full-time whistle
  result: 'won' | 'drawn' | 'lost';   // for the club, which is always the home side (the screen is at its ground)
  leaguePoints: number | null;    // league matches only, bonus points included
  home: RecordSide; away: RecordSide;
  /** every score and card in order */
  timeline: { min: string; team: Side; label: string; who: string }[];
}

/** The season a date falls in. English club rugby and football seasons run from August to the following May. */
export function seasonOf(ms: number): string {
  const d = new Date(ms), y = d.getUTCFullYear(), start = d.getUTCMonth() >= 7 ? y : y - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

/** Only matches that kicked off belong in the record; a match set up and never started is left out. */
export const wasPlayed = (s: MatchState | null): s is MatchState => !!s && s.phase !== 'pre';

/** Turn a match into its season record entry, with the club's player names as they are today. */
export function matchRecord(s: MatchState, club: Club | null): MatchRecord {
  const rules = getRules(s.rulesId), sum = matchSummary(s, rules);
  const players = new Map(Object.values(club?.squads ?? {}).flatMap(sq => sq.players).map(p => [p.id, p]));
  const nameOf = (id: string) => { const p = players.get(id); return p ? (p.name || (p.number ? `No. ${p.number}` : 'Unnamed player')) : 'Unknown player'; };
  const team = (side: Side) => side === 'home'
    ? (s.lineups?.home ?? []).map(l => ({ number: l.number, name: nameOf(l.player) }))
    : (s.lineups?.away ?? []).map(l => ({ number: l.number, name: l.name }));
  const teams = { home: team('home'), away: team('away') };
  /** A card names the shirt number; today's team turns it into a name when it can. */
  const cardWho = (side: Side, number?: string | null) => {
    if (!number) return '';
    const named = teams[side].find(p => p.number === number);
    return named ? `${named.name} (${number})` : `No. ${number}`;
  };
  const side = (k: Side): RecordSide => {
    const x = sum[k], tm = s.teams[k];
    return {
      name: tm.name, short: tm.short, color: tm.color, crest: tm.crest ?? null,
      points: x.points, tries: x.tries, counts: x.counts.map(c => ({ label: c.label, n: c.n })),
      scorers: x.scorers.map(sc => ({ name: sc.player ? nameOf(sc.player) : 'Own goal', n: sc.tries, mins: sc.mins })),
      bonus: x.bonus,
      cards: s.events.flatMap(e => e.t === 'card' && e.team === k
        ? [{ label: rules.cards.find(c => c.card === e.card)?.label ?? 'Card', who: cardWho(k, e.number), min: minuteOf(e, rules) }] : []),
      team: teams[k],
    };
  };
  const result = sum.result === 'draw' ? 'drawn' : sum.result === 'home' ? 'won' : 'lost';
  const home = side('home'), away = side('away');
  const league = s.league !== false;
  const squadLabel = s.squad ? club?.squads[s.squad]?.label ?? null : null;
  return {
    v: 1, id: s.matchId, date: s.created, season: seasonOf(s.created), sport: rules.id,
    squad: s.squad, squadLabel, league, finished: s.phase === 'FT', result,
    leaguePoints: league ? (result === 'won' ? rules.leaguePoints.win : result === 'drawn' ? rules.leaguePoints.draw : 0) + home.bonus.length : null,
    home, away,
    timeline: s.events.map(e => e.t === 'score'
      ? { min: minuteOf(e, rules), team: e.team, label: rules.scoring.find(r => r.kind === e.kind)?.label ?? e.kind, who: e.team === 'home' && e.player ? nameOf(e.player) : '' }
      : { min: minuteOf(e, rules), team: e.team, label: rules.cards.find(c => c.card === e.card)?.label ?? 'Card', who: cardWho(e.team, e.number) }),
  };
}

export interface SeasonTotals {
  played: number; won: number; drawn: number; lost: number;
  pointsFor: number; pointsAgainst: number;
  /** league matches only */
  leaguePoints: number; bonus: number;
  /** our try or goal scorers across the season, most first */
  scorers: { name: string; n: number }[];
}

/** Add up a list of matches: the record line and the season's top scorers. */
export function seasonTotals(records: MatchRecord[]): SeasonTotals {
  const out: SeasonTotals = { played: 0, won: 0, drawn: 0, lost: 0, pointsFor: 0, pointsAgainst: 0, leaguePoints: 0, bonus: 0, scorers: [] };
  const by = new Map<string, number>();
  for (const r of records) {
    out.played++; out[r.result]++;
    out.pointsFor += r.home.points; out.pointsAgainst += r.away.points;
    if (r.league) { out.leaguePoints += r.leaguePoints ?? 0; out.bonus += r.home.bonus.length; }
    for (const sc of r.home.scorers) if (sc.name !== 'Own goal') by.set(sc.name, (by.get(sc.name) ?? 0) + sc.n);
  }
  out.scorers = [...by].map(([name, n]) => ({ name, n })).sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
  return out;
}

const cell = (v: string | number) => { const s = String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
/** The season as a spreadsheet: one row per match. */
export function seasonCsv(records: MatchRecord[], scoreWord = 'Tries'): string {
  const head = ['Date', 'Team', 'Opponent', 'Competition', 'Result', 'For', 'Against', scoreWord, 'Scorers', 'Bonus points', 'League points', 'Cards', 'Full time'];
  const rows = records.map(r => [
    new Date(r.date).toISOString().slice(0, 10), r.squadLabel ?? '', r.away.name, r.league ? 'League' : 'Cup or friendly',
    r.result === 'won' ? 'W' : r.result === 'drawn' ? 'D' : 'L', r.home.points, r.away.points,
    r.home.tries,
    r.home.scorers.map(s => `${s.name}${s.n > 1 ? ` ${s.n}` : ''}`).join('; '),
    r.league ? r.home.bonus.length : '', r.leaguePoints ?? '',
    r.home.cards.map(c => `${c.label}${c.who ? ` ${c.who}` : ''} ${c.min}`).join('; '),
    r.finished ? 'Yes' : 'Ended early',
  ]);
  return [head, ...rows].map(r => r.map(cell).join(',')).join('\r\n') + '\r\n';
}
